import { createFileRoute } from '@tanstack/react-router'
import { FileDown } from 'lucide-react'
import { useState } from 'react'

import { Calendar } from '@/components/icons'
import { PageLayout } from '@/components/layout'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

import { toIdr } from '@/libs/utils/format'

export const Route = createFileRoute('/_app/report/balance-sheet')({
  component: RouteComponent,
  loader: () => {
    return {
      crumb: 'Laporan Neraca',
    }
  },
})

function RouteComponent() {
  const [outletId, setOutletId] = useState('ikki_coffee')
  const [period] = useState('2025-11')

  // Gunakan mock data berdasarkan outlet
  const data = mockBalanceSheetData[outletId]
  return (
    <PageLayout>
      <PageLayout.Header title='Laporan Neraca' />

      <BalanceSheetHeader
        outletId={outletId}
        period={period}
        onOutletChange={setOutletId}
        onPeriodChange={() => {}}
      />
      <BalanceSheetTable data={data} />
    </PageLayout>
  )
}

interface Account {
  code: string
  name: string
  balance: number
  level: number
  isHeader: boolean
}

interface BalanceSheetData {
  assets: Account[]
  liabilities: Account[]
  equity: Account[]
  totalAssets: number
  totalLiabilities: number
  totalEquity: number
}

interface BalanceSheetTableProps {
  data: BalanceSheetData
}

export function BalanceSheetTable({ data }: BalanceSheetTableProps) {
  const renderSection = (title: string, accounts: Account[]) => (
    <Card className='mb-6'>
      <CardHeader>
        <CardTitle className='text-lg font-semibold'>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className='w-full'>Akun</TableHead>
              <TableHead className='text-right'>Saldo</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {accounts.map(acc => (
              <TableRow
                key={acc.code}
                className={
                  acc.isHeader
                    ? `
                      bg-gray-50 font-bold
                      dark:bg-gray-800
                    `
                    : ''
                }
              >
                <TableCell style={{ paddingLeft: `${acc.level * 24}px` }}>
                  {acc.isHeader ? acc.name : `${acc.code} - ${acc.name}`}
                </TableCell>
                <TableCell className='text-right font-medium'>{toIdr(acc.balance)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )

  return (
    <div>
      {renderSection('ASET', data.assets)}
      {renderSection('KEWAJIBAN', data.liabilities)}
      {renderSection('EKUITAS', data.equity)}

      <Card
        className={`
          border-blue-200
          dark:border-blue-800
        `}
      >
        <CardContent className='pt-6'>
          <div className='flex items-center justify-between text-lg font-bold'>
            <span>Total Aset</span>
            <span
              className={`
                text-blue-600
                dark:text-blue-400
              `}
            >
              {toIdr(data.totalAssets)}
            </span>
          </div>
          <div className='mt-2 flex items-center justify-between text-lg font-bold'>
            <span>Total Kewajiban + Ekuitas</span>
            <span
              className={`
                text-green-600
                dark:text-green-400
              `}
            >
              {toIdr(data.totalLiabilities + data.totalEquity)}
            </span>
          </div>
          <div
            className={`
              mt-4 rounded-lg bg-green-50 p-3 text-center text-sm font-medium text-green-700
              dark:bg-green-900/20 dark:text-green-300
            `}
          >
            Neraca Seimbang
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

const mockBalanceSheetData: Record<string, BalanceSheetData> = {
  ikki_coffee: {
    assets: [
      { code: '10000', name: 'ASET LANCAR', balance: 0, level: 1, isHeader: true },
      { code: '10100', name: 'Kas di Tangan', balance: 15230000, level: 2, isHeader: false },
      { code: '10110', name: 'Kas di Bank', balance: 8200000, level: 2, isHeader: false },
      { code: '10300', name: 'Persediaan Bahan Baku', balance: 9850000, level: 2, isHeader: false },
      { code: '11000', name: 'ASET TETAP', balance: 0, level: 1, isHeader: true },
      {
        code: '11400',
        name: 'Peralatan Dapur & Toko',
        balance: 45000000,
        level: 2,
        isHeader: false,
      },
      { code: '11500', name: 'Akumulasi Penyusutan', balance: -7500000, level: 2, isHeader: false },
    ],
    liabilities: [
      { code: '20000', name: 'KEWAJIBAN LANCAR', balance: 0, level: 1, isHeader: true },
      { code: '20100', name: 'Hutang Usaha', balance: 6200000, level: 2, isHeader: false },
      { code: '20200', name: 'Hutang Pajak', balance: 1850000, level: 2, isHeader: false },
    ],
    equity: [
      { code: '30000', name: 'EKUITAS', balance: 0, level: 1, isHeader: true },
      { code: '30010', name: 'Modal Pemilik', balance: 50000000, level: 2, isHeader: false },
      { code: '31000', name: 'Laba Ditahan', balance: 12000000, level: 2, isHeader: false },
      {
        code: '32000',
        name: 'Laba/Rugi Tahun Berjalan',
        balance: 4850000,
        level: 2,
        isHeader: false,
      },
    ],
    totalAssets: 15230000 + 8200000 + 9850000 + (45000000 - 7500000),
    totalLiabilities: 6200000 + 1850000,
    totalEquity: 50000000 + 12000000 + 4850000,
  },

  ikki_resto: {
    assets: [
      { code: '10000', name: 'ASET LANCAR', balance: 0, level: 1, isHeader: true },
      { code: '10100', name: 'Kas di Tangan', balance: 18900000, level: 2, isHeader: false },
      { code: '10110', name: 'Kas di Bank', balance: 12300000, level: 2, isHeader: false },
      {
        code: '10300',
        name: 'Persediaan Bahan Baku',
        balance: 14200000,
        level: 2,
        isHeader: false,
      },
      { code: '11000', name: 'ASET TETAP', balance: 0, level: 1, isHeader: true },
      {
        code: '11400',
        name: 'Peralatan Dapur & Toko',
        balance: 68000000,
        level: 2,
        isHeader: false,
      },
      {
        code: '11500',
        name: 'Akumulasi Penyusutan',
        balance: -11300000,
        level: 2,
        isHeader: false,
      },
    ],
    liabilities: [
      { code: '20000', name: 'KEWAJIBAN LANCAR', balance: 0, level: 1, isHeader: true },
      { code: '20100', name: 'Hutang Usaha', balance: 9800000, level: 2, isHeader: false },
      { code: '20200', name: 'Hutang Pajak', balance: 2100000, level: 2, isHeader: false },
    ],
    equity: [
      { code: '30000', name: 'EKUITAS', balance: 0, level: 1, isHeader: true },
      { code: '30010', name: 'Modal Pemilik', balance: 70000000, level: 2, isHeader: false },
      { code: '31000', name: 'Laba Ditahan', balance: 18500000, level: 2, isHeader: false },
      {
        code: '32000',
        name: 'Laba/Rugi Tahun Berjalan',
        balance: 7200000,
        level: 2,
        isHeader: false,
      },
    ],
    totalAssets: 18900000 + 12300000 + 14200000 + (68000000 - 11300000),
    totalLiabilities: 9800000 + 2100000,
    totalEquity: 70000000 + 18500000 + 7200000,
  },
}

interface BalanceSheetHeaderProps {
  outletId: string
  period: string
  onOutletChange: (id: string) => void
  onPeriodChange: (period: string) => void
}

const OUTLETS = [
  { id: 'ikki_coffee', name: 'Ikki Coffee' },
  { id: 'ikki_resto', name: 'Ikki Resto' },
]

export function BalanceSheetHeader({ outletId, period, onOutletChange }: BalanceSheetHeaderProps) {
  return (
    <div
      className={`
        mb-6 rounded-lg bg-white p-6 shadow-sm
        dark:bg-gray-800
      `}
    >
      <div
        className={`
          flex flex-col items-start justify-between gap-4
          md:flex-row md:items-center
        `}
      >
        <div>
          <h1
            className={`
              text-2xl font-bold text-gray-900
              dark:text-white
            `}
          >
            Laporan Neraca
          </h1>
          <p
            className={`
              mt-1 text-sm text-gray-500
              dark:text-gray-400
            `}
          >
            Posisi keuangan per outlet
          </p>
        </div>

        <div className='flex gap-3'>
          <Button variant='outline' size='sm' disabled>
            <FileDown className='mr-2 h-4 w-4' />
            Export PDF
          </Button>
          <Button variant='outline' size='sm' disabled>
            <FileDown className='mr-2 h-4 w-4' />
            Export Excel
          </Button>
        </div>
      </div>

      <div
        className={`
          mt-6 grid grid-cols-1 gap-4
          md:grid-cols-2
        `}
      >
        <div>
          <Label htmlFor='outlet'>Outlet</Label>
          <Select value={outletId} onValueChange={onOutletChange}>
            <SelectTrigger id='outlet'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {OUTLETS.map(o => (
                <SelectItem key={o.id} value={o.id}>
                  {o.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor='period'>Periode</Label>
          <div className='relative'>
            <Input
              id='period'
              type='month'
              value={period}
              readOnly
              className='cursor-not-allowed bg-gray-50 pl-10'
            />
            <Calendar className='absolute top-3 left-3 h-4 w-4 text-gray-400' />
          </div>
        </div>
      </div>
    </div>
  )
}

interface Account {
  code: string
  name: string
  balance: number
  level: number
  isHeader: boolean
}

interface BalanceSheetData {
  assets: Account[]
  liabilities: Account[]
  equity: Account[]
  totalAssets: number
  totalLiabilities: number
  totalEquity: number
}

interface Props {
  data: BalanceSheetData
}
